//@ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  FormControl,
  MenuItem,
  Select,
  Box,
  DialogTitle,
  DialogContent,
  Alert,
  Checkbox,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import TrainingList from "../TrainingList";
import ImageUpload from "../ImageUpload";
import axios from "axios";
import { useGetToken } from "../../hooks/useGetToken";
import { validateField } from "../helper";
import { useGlobalStateContext } from "../../context/globalState";
import StatusCardProps from "../StatusCardProps";
import AlertBox from "../AlertBox";
import TextLabel from "../TextLabel";
import { CONSTANTS } from "../../CONSTANTS";
import StyledTextField from "../../styledComponents/styledTextField";
import StyledInputLabel from "../../styledComponents/styledInputLabel";
import StyledButtons from "../../styledComponents/styledButtons";
import StyledDialog from "../../styledComponents/styledDialog";
import { useTranslation } from "react-i18next";
import Loading from "../Loading";
import CredentialsDialog, { LoginCredentials } from "../CredentialsDialog";
import { fetchBusinessTypes } from "../../services/TrainingsHeaderViewService";
import { apiErrorMessage } from "../../utils/apiUtils";
import {
  baseFieldRulesFrom,
  getFieldSchema,
} from "../../services/countryConfigService";

interface InstallerFormValues {
  firstName: string;
  lastName: string;
  emailId: string;
  contactNumber: string;
  assessorName: string;
  koneEmployeeId?: string;
  passportType: number;
  region: string;
  frontline: string;
  country: string;
  company: string;
  role: string;
  qualified: string;
  qualificationType: string;
  comments: string;
  photo: string;
  fileType: string;
  status: string;
  statusStartDate: string | null; // Allow null for active status
  businessTypeId: number[]; // Business type IDs
  managerId?: string; // users.id of the chosen foreman, subcontractors only
}

const AdminInstallerForm: React.FC = ({
  openForm,
  onCloseCb,
  onUpdateInstallers,
  region,
  frontline,
  company,
  countryCode,
}) => {
  const [getToken] = useGetToken();
  const { t } = useTranslation();
  const {
    regionId,
    setRegionId,
    supplierType,
    blockReason,
    loading,
    beginLoading,
    endLoading,
    setUserFirstName,
    setUserLastName,
    qualificationsList,
    setQualificationsList,
    passportTypeId,
    setPassportTypeId,
    qualificationTypeId,
    setQualificationTypeId,
    userPreference,
    selectedBusinessType,
    selectedBusinessTypeId,
    businessTypesList,
    setBusinessTypesList,
    statusEndDate,
    adminRole,
  } = useGlobalStateContext();
  const [countryList, setCountryList] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [frontlineList, setFrontlineList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [installerRoleList, setInstallerRoleList] = useState([]);
  // Manager (foreman) candidates for the selected vendor. Subcontractors only.
  const [managerList, setManagerList] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  // Vendor of the most recent manager request, so a slower earlier one cannot
  // overwrite it when the responses come back out of order.
  const latestManagerVendorRef = useRef("");
  const [trainingData, setTrainingData] = useState<any[]>([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedCountry, setSelectedCountry] = React.useState("");
  const [countryCodes, setCountryCodes] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([]);
  // Per-country rules for the built-in fields, keyed by their schema field key.
  const [baseRules, setBaseRules] = useState({});
  const [koneEmployeeId, setkoneEmployeeId] = useState("");
  const [dynamicFieldValues, setDynamicFieldValues] = useState<{
    [key: string]: string;
  }>({});
  const [dynamicFieldErrors, setDynamicFieldErrors] = useState<{
    [key: string]: string;
  }>({});
  const [values, setValues] = useState<InstallerFormValues>({
    firstName: "",
    lastName: "",
    emailId: "",
    contactNumber: "",
    assessorName: "",
    koneEmployeeId: "",
    region: "",
    passportType: "",
    frontline: "",
    country: "",
    company: "",
    role: "",
    qualified: [],
    qualificationType: "",
    comments: "",
    photo: "",
    fileType: "",
    status: "active",
    statusStartDate: "",
    businessTypeId: selectedBusinessTypeId ? [selectedBusinessTypeId] : [], // Default to selected business type ID from dashboard
    customRoleName: "", // Add this field for the "Others" description
    managerId: "", // Chosen foreman, subcontractor employees only
  });

  // Manage dialog open state
  const [openDialog, setOpenDialog] = useState(true);
  // Generated login credentials for a newly created subcontractor employee,
  // shown once in a dialog. Null for KONE users (no credentials returned).
  const [credentials, setCredentials] = useState<LoginCredentials | null>(null);

  // Handle Error Alert Box
  const [alertBoxState, setAlertBoxState] = useState({
    type: "",
    message: "",
  });

  // Handle Qualification Alert
  const [qualificationAlertMessage, setQualificationAlertMessage] =
    useState("");

  const handleCancel = (event, reason) => {
    // Prevent closing on backdrop click or escape key, allow only explicit cancel
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    if (event?.type === "keydown" && event.key !== "Enter") return;
    // Reset form values and close dialog
    onCloseCb(event);
    setOpenDialog(false); // Close dialog on cancel
    localStorage.removeItem("selectedQualifications");
    setUserFirstName("");
    setUserLastName("");
  };

  const handleChange = async (field: keyof InstallerFormValues, value: any) => {
    // Handle business type change - reset dependent fields and re-fetch roles
    if (field === "businessTypeId") {
      const selectedBusinessTypes = Array.isArray(value)
        ? value.map((id) => Number(id))
        : [];
      setValues((prev) => ({
        ...prev,
        [field]: selectedBusinessTypes,
        // To be implement the following resets if needed in the future:
        // region: "", // Reset region when business type changes
        // frontline: "", // Reset frontline when business type changes
        // country: "", // Reset country when business type changes
        // company: "", // Reset company when business type changes
        role: "", // Reset role when business type changes
        // qualified: [], // Reset qualified countries when business type changes
      }));
      // setCountryList([]);
      setSelectedCountry("");
      // setFrontlineList([]);
      setInstallerRoleList([]);
      // Fetch roles for the selected business type ID
      if (selectedBusinessTypes.length > 0) {
        fetchRolesData(selectedBusinessTypes);
      }
    }
    // Handle dependent field resets
    else if (field === "frontline") {
      setValues((prev) => ({
        ...prev,
        [field]: value,
        country: "", // Reset country when frontline changes
        company: "", // Reset company when frontline changes
        qualified: [], // Reset qualified countries when frontline changes
      }));
      setCountryList([]);
      setSelectedCountry("");
      await fetchCountryData(value);
    } else if (field === "company" && supplierType === "vendor") {
      // The candidate managers belong to the vendor, so changing the vendor
      // invalidates any manager already chosen. The list itself is re-fetched
      // by the effect watching values.company.
      setValues((prev) => ({ ...prev, [field]: value, managerId: "" }));
    } else {
      setValues((prev) => ({ ...prev, [field]: value }));
    }

    if (field === "country") {
      setSelectedCountry(getCountryCodeById(value));
    }
    if (field === "firstName") {
      setUserFirstName(value);
    }
    if (field === "lastName") {
      setUserLastName(value);
    }
    if (field === "status") {
      // If status is being set then set the statusStartDate to current date
      // If status is active then set the statusStartDate to null, else set it to current date.
      // This is to handle the case when status is changed from active to inactive or blocked.
      if (value === "active") {
        setValues((prev) => ({
          ...prev,
          statusStartDate: null,
          statusEndDate: null,
        }));
      } else {
        setValues((prev) => ({
          ...prev,
          statusStartDate: new Date().toISOString(),
        }));
      }
    }
    const error = validateField(field, value, supplierType === "vendor");
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }));
  };

  const getCountryCodeById = (countryId) => {
    return countryList.find((country) => country.country_id === countryId)
      ?.country_code;
  };

  // Function to handle the close event of the alert box
  const handleCloseAlert = () => {
    setAlertBoxState({ type: "", message: "" });
  };

  // Handle Training Edit Click Validation
  const handleQualificationEditClick = () => {
    if (!values.country || values.country === "") {
      setQualificationAlertMessage(
        t(
          "adminDashboard.form.addInstaller.errorMessage.qualifiedCountryRequired",
        ),
      );
      return false;
    }
    if (adminRole === CONSTANTS.ADMIN_ROLES.SUBCON_ADMIN ) {
      setQualificationAlertMessage(
        t(
          "adminDashboard.form.addInstaller.errorMessage.subconAdminQualificationRestriction",
        ),
      );
      return false;
    }
    return true;
  };

  // Auto-remove qualification alert when country is selected
  useEffect(() => {
    if (values.country && values.country !== "" && qualificationAlertMessage) {
      setQualificationAlertMessage("");
    }
  }, [values.country]);

  // GET dynamic (custom) fields for the selected country from the generic
  // country-config engine. Falls back gracefully (empty) if the endpoint is
  // unavailable so the base form always works.
  //
  // The schema is requested for THIS user's audience: a field targeted at
  // subcontractors must not appear while creating a KONE internal user, and one
  // targeted at specific business types must not appear for the others. The
  // server owns that rule (`filterFieldsForAudience`), so the audience is sent
  // rather than re-implemented here.
  const getDynamicFieldsForCountry = async (countryId, businessTypeIds) => {
    const token = await getToken();
    try {
      const schema = await getFieldSchema(countryId, token, {
        companyType: supplierType === "vendor" ? "subcon" : "kone",
        businessTypeIds: businessTypeIds || [],
      });
      // The built-in fields are rendered natively rather than from the schema,
      // so their per-country visibility and mandatory overrides have to be
      // picked out here - otherwise hiding one in the country config would have
      // no effect on this form.
      setBaseRules(baseFieldRulesFrom(schema || []));
      // Custom fields are rendered straight from the schema.
      const mapped = (schema || [])
        .filter((item) => item.source === "custom" && item.isVisible)
        .map((item) => ({
          fieldKey: item.key,
          displayName: item.displayName || item.key,
          fieldType: item.fieldType,
          isMandatory: item.isMandatory ? 1 : 0,
          charLimit: item.charLimit,
          fieldOptions: item.fieldOptions,
        }));
      setDynamicFields(mapped);
      pruneDynamicFieldValues(mapped);
    } catch (error) {
      console.error("Error fetching dynamic fields:", error);
      setDynamicFields([]);
      // Base rules are per country too. Leaving the previous country's behind
      // would keep a field hidden, or keep it mandatory, according to a country
      // this form is no longer on.
      setBaseRules({});
    }
  };

  /**
   * Drop anything typed into a field that no longer applies.
   *
   * Changing the business type can narrow the schema, and a value left behind
   * for a field the admin can no longer see would still be submitted - and
   * rejected by the server, which validates against the same audience.
   */
  const pruneDynamicFieldValues = (visibleFields: any[]) => {
    const keep = new Set(visibleFields.map((f) => f.fieldKey));
    const drop = (prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (keep.has(key)) next[key] = prev[key];
      });
      return next;
    };
    setDynamicFieldValues(drop);
    setDynamicFieldErrors(drop);
  };

  // Fetch custom fields whenever the country or the business types change -
  // both are inputs to which fields apply.
  useEffect(() => {
    if (values.country) {
      getDynamicFieldsForCountry(values.country, values.businessTypeId);
    } else {
      setDynamicFields([]);
      setDynamicFieldValues({});
      setDynamicFieldErrors({});
      setBaseRules({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.country, values.businessTypeId, supplierType]);

  // Handle dynamic field change with validation
  const handleDynamicFieldChange = (
    fieldKey: string,
    value: string,
    field: any,
  ) => {
    // For text fields, validate character limit
    if (field.fieldType === "text" && field.charLimit) {
      if (value.length > field.charLimit) {
        setDynamicFieldErrors((prev) => ({
          ...prev,
          [fieldKey]: t("validation.maxCharactersAllowed", { charLimit: field.charLimit }),
        }));
        return; // Don't update value if it exceeds limit
      } else {
        // Clear error if within limit
        setDynamicFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldKey];
          return newErrors;
        });
      }
    }

    // Update the dynamic field value
    setDynamicFieldValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  // Validate dynamic fields
  const validateDynamicFields = () => {
    const errors: { [key: string]: string } = {};

    dynamicFields.forEach((field: any) => {
      const value = dynamicFieldValues[field.fieldKey];

      // Check if mandatory field is empty
      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        value === false ||
        (typeof value === "string" && value.trim() === "");
      if (field.isMandatory === 1 && isEmpty) {
        errors[field.fieldKey] = t("validation.displayNameRequired", {
          displayName: field.displayName,
        });
      }

      // Check character limit for text fields
      if (
        field.fieldType === "text" &&
        field.charLimit &&
        typeof value === "string" &&
        value.length > field.charLimit
      ) {
        errors[field.fieldKey] = t("validation.maxCharactersAllowed", {
          charLimit: field.charLimit,
        });
      }
    });

    return errors;
  };

  // Handle image change (receive Base64 image and store it in form state)
  const handleImageChange = (base64Image: string, fileType: string) => {
    setValues({
      ...values,
      photo: base64Image, // Set the Base64 image string to the form state
      fileType: fileType,
    });
  };

  // Handle form submission
  // Reset the form fields and close the dialog. Shared by the KONE success path
  // (immediate) and the subcontractor path (after the credentials dialog closes).
  const resetAndCloseForm = (e?: any) => {
    setValues({
      firstName: "",
      lastName: "",
      emailId: "",
      contactNumber: "",
      assessorName: "",
      koneEmployeeId: "",
      passportLevel: "",
      frontline: "",
      country: "",
      company: "",
      role: "",
      qualified: [],
      qualificationType: "",
      comments: "",
      photo: "",
      status: "active",
      businessTypeId: [],
      managerId: "",
    });
    setManagerList([]);
    setPassportTypeId("");
    setOpenDialog(false);
    onCloseCb(e);
  };

  const handleCredentialsClose = () => {
    setCredentials(null);
    resetAndCloseForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const loadingToken = beginLoading("admin-installer-submit");
    setErrors({}); // Reset errors before validation
    setDynamicFieldErrors({}); // Reset dynamic field errors
    setAlertBoxState({ type: "", message: "" }); // Reset alert box state
    const errors: { field: string; error: string }[] = [];

    // Validate dynamic fields
    const dynamicErrors = validateDynamicFields();
    if (Object.keys(dynamicErrors).length > 0) {
      setDynamicFieldErrors(dynamicErrors);
      endLoading(loadingToken);
      return;
    }

    const { businessTypeId, statusStartDate, ...valuesWithoutBusinessTypeId } = values;
    // Send only the fields that apply to this user. The server validates
    // against the same audience, so a value for a field it filtered out would
    // be an unknown key at best and a rejection at worst.
    const dynamicFieldValuesToSend = {};
    dynamicFields.forEach((field: any) => {
      if (dynamicFieldValues[field.fieldKey] !== undefined)
        dynamicFieldValuesToSend[field.fieldKey] =
          dynamicFieldValues[field.fieldKey];
    });
    // Prepare the data to send
    const formData = {
      ...valuesWithoutBusinessTypeId,
      trainingData: qualificationsList,
      status_reason: blockReason,
      status_start_date: statusStartDate || '', // Set current date as status start date
      status_end_date: statusEndDate || "", // Use statusEndDate from context, default to empty string if not set
      passportType: passportTypeId,
      qualificationType: qualificationTypeId,
      businessTypeIds: businessTypeId || [], // Send selected business type IDs
      ...dynamicFieldValuesToSend, // Include dynamic field values
    };

    const businessTypeError = validateField(
      "businessTypeId",
      businessTypeId,
      supplierType === "vendor",
    );
    if (businessTypeError) {
      errors.push({ field: "businessTypeId", error: businessTypeError });
    }
    for (const key in formData) {
      // A field the country hides is not rendered, so it must not be required.
      if (!showBaseField(key)) continue;
      const error = validateField(
        key,
        formData[key],
        supplierType === "vendor",
      );
      if (error) {
        errors.push({ field: key, error });
      }
    }
    if (errors.length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...Object.fromEntries(errors.map(({ field, error }) => [field, error])),
      }));
      endLoading(loadingToken); // Reset loading state
      return;
    }
    try {
      const token = await getToken(); // Get the token from your custom hook
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token, // Include the token in the header if required
        },
      };

      // The form holds these under the catalog's canonical names; the create
      // endpoint reads `email` and `phoneNumber`, so translate once here at the
      // boundary rather than letting the endpoint's naming leak into the form.
      const { emailId, contactNumber, ...rest } = formData;
      const requestBody = {
        ...rest,
        email: emailId,
        phoneNumber: contactNumber,
      };

      // Send the POST request to your API endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/installers/add?${supplierType === "vendor" ? "isSub=true" : null}`,
        requestBody,
        config,
      );

      // Login credentials generated for a subcontractor employee (undefined for
      // KONE users). Captured before the form is reset/closed.
      const returnedCredentials = response.data?.credentials;

      if (response.data) {
        const businessTypeValue = selectedBusinessTypeId || 100; // Default to 100 if no business type is selected
        const updatedResponse = await axios.get(
          `${process.env.REACT_APP_API_ENDPOINT}/v1/installers/${company}?businessTypeId=${businessTypeValue}`,
          config,
        );
        onUpdateInstallers(updatedResponse.data.users);
        if (updatedResponse.data) {
          setSuccessMessage(t("successMessages.userAddedSuccessfully"));
          setShowAlert(true); // Show alert
        }
      }

      // When credentials were generated, keep this form mounted and show the
      // credentials dialog on top; the form is reset/closed only when the admin
      // dismisses that dialog (see handleCredentialsClose). Otherwise close now.
      if (returnedCredentials) {
        setCredentials(returnedCredentials);
      } else {
        resetAndCloseForm(e);
      }
    } catch (error) {
      // Handle error if the request fails
      console.error("Error submitting form:", error);
      setAlertBoxState({
        type: "error",
        message: apiErrorMessage(error, t("error_messages.formSubmitError")),
      });
    } finally {
      endLoading(loadingToken);
      setUserFirstName("");
      setUserLastName("");
    }
  };

  const fetchRegionsData = async () => {
    const loadingToken = beginLoading("admin-installer-regions");
    const token = await getToken();
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/regions?lang=${userPreference.languagePreference}`,
        config,
      );
      setRegionList(response.data);
    } catch (error) {
      console.error("Error fetching regions:", error);
      setAlertBoxState({
        type: "error",
        message: t("error_messages.fetchRegionsError"),
      });
    } finally {
      endLoading(loadingToken);
    }
  };

  const fetchCountryData = async (frontlineId) => {
    if (!frontlineId) return;
    const loadingToken = beginLoading("admin-installer-countries");
    const token = await getToken();
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/countries?frontlineId=${frontlineId}&lang=${userPreference.languagePreference}`,
        config,
      );
      setCountryList(response.data);
      // setCountryCodes(response.data.map((country) => ({ code: country.phone_code, country: country.name })))
    } catch (error) {
      console.error("Error fetching countries:", error);
      setAlertBoxState({
        type: "error",
        message: t("error_messages.fetchCountriesError"),
      });
    } finally {
      endLoading(loadingToken);
    }
  };
  const fetchFrontlineData = async () => {
    if (!regionId) return;
    const loadingToken = beginLoading("admin-installer-frontlines");
    const token = await getToken();
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/frontlines/${regionId}?lang=${userPreference.languagePreference}`,
        config,
      );
      setFrontlineList(response.data);
    } catch (error) {
      console.error("Error fetching frontlines:", error);
      setAlertBoxState({
        type: "error",
        message: t("error_messages.fetchFrontlinesError"),
      });
    } finally {
      endLoading(loadingToken);
    }
  };

  const fetchVendorsData = async () => {
    if (!supplierType) {
      setAlertBoxState({
        type: "error",
        message: t(
          "adminDashboard.form.addInstaller.errorMessage.supplierTypeRequired",
        ),
      });
      return;
    }
    const loadingToken = beginLoading("admin-installer-vendors");
    const token = await getToken();
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      if (selectedCountry) {
        const businessTypeParam = values.businessTypeId.join(",");
        const response = await axios.get(
          supplierType === "vendor"
            ? `${process.env.REACT_APP_API_ENDPOINT}/v1/company/getVendors?countries="${selectedCountry}"&frontline=${values.frontline}&limit=-1`
            : `${process.env.REACT_APP_API_ENDPOINT}/v1/company/getCompany?countries="${selectedCountry}"&limit=-1&lang=${userPreference.languagePreference}&businessTypeId=${businessTypeParam}`,
          config,
        );
        const finalResponse =
          supplierType === "supplier"
            ? response.data.company
            : response.data.vendors;
        setCompanyList(finalResponse);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setAlertBoxState({
        type: "error",
        message: t("error_messages.fetchDataError"),
      });
    } finally {
      endLoading(loadingToken);
    }
  };
  // Manager candidates for one vendor. A manager must be in the same company as
  // the person reporting to them, so this is always scoped by vendor and is
  // re-fetched whenever the selected vendor changes.
  const fetchManagersData = async (vendorId: string) => {
    // Responses can arrive out of order when the vendor is changed twice. Only
    // the newest request may write, or the list would show another company's
    // people while the form points at this one.
    latestManagerVendorRef.current = vendorId;
    setManagerList([]);
    if (!vendorId) {
      return;
    }
    setManagersLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/installers/managers?vendorId=${vendorId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );
      if (latestManagerVendorRef.current !== vendorId) return;
      setManagerList(response.data?.managers || []);
    } catch (error) {
      // A missing candidate list must not block onboarding - the manager is
      // optional, so the field simply stays empty.
      console.error("Error fetching managers:", error);
    } finally {
      if (latestManagerVendorRef.current === vendorId) {
        setManagersLoading(false);
      }
    }
  };

  const fetchRolesData = async (businessTypeIds?: number[]) => {
    const loadingToken = beginLoading("admin-installer-roles");
    const token = await getToken();
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      let url = `${process.env.REACT_APP_API_ENDPOINT}/v1/roles?lang=${userPreference.languagePreference}`;
      if (businessTypeIds && businessTypeIds.length > 0) {
        url += `&businessTypeId=${businessTypeIds.join(",")}`;
      }
      if (supplierType === "vendor") {
        url += `&companyType=subcontractor`;
      } else {
        url += `&companyType=kone`;
      }

      const response = await axios.get(url, config);
      setInstallerRoleList(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setAlertBoxState({
        type: "error",
        message: t("error_messages.fetchRolesError"),
      });
    } finally {
      endLoading(loadingToken);
    }
  };

  // Fetch business types on mount
  const fetchBusinessTypesData = async () => {
    const token = await getToken();
    try {
      const businessTypesData = await fetchBusinessTypes(
        token,
        userPreference.languagePreference,
      );
      setBusinessTypesList(businessTypesData);
    } catch (error) {
      console.error("Error fetching business types:", error);
    }
  };

  const fetchCompanyData = async () => {
    const loadingToken = beginLoading("admin-installer-companies");
    const token = await getToken();
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/company/listCompany`,
        config,
      );
      setCompanyList(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      endLoading(loadingToken);
    }
  };

  useEffect(() => {
    if (
      values.frontline &&
      countryCode &&
      countryList.length > 0 &&
      !values.country
    ) {
      const _countryId =
        countryList.find((country) => country.country_code === countryCode)
          ?.country_id ?? "";
      setValues((prev) => ({ ...prev, country: _countryId }));
      setSelectedCountry(countryCode);
    }
  }, [values.frontline, countryCode, countryList.length]);

  useEffect(() => {
    if (!values.frontline) {
      setValues((prev) => ({ ...prev, frontline: frontline }));
      fetchCountryData(frontline);
    }
  }, [frontlineList.length]);
  useEffect(() => {
    if (!values.company) {
      setValues((prev) => ({ ...prev, company: company }));
    }
  }, [company]);

  // Candidate managers follow the selected vendor. Keyed on the value rather
  // than the change handler because a subcontractor admin has a single vendor
  // that is pre-filled above, so the dropdown they would change never fires.
  useEffect(() => {
    if (supplierType === "vendor" && values.company) {
      fetchManagersData(String(values.company));
    } else {
      setManagerList([]);
    }
  }, [values.company, supplierType]);

  useEffect(() => {
    fetchVendorsData();
  }, [selectedCountry]);

  useEffect(() => {
    // fetchCountryData()
    // clear qualifications list if it exists
    if (qualificationsList.length > 0) {
      setQualificationsList([]);
    }
    fetchBusinessTypesData(); // Fetch business types on mount
    fetchVendorsData();
    // Only fetch roles if business type is selected
    if (values.businessTypeId.length > 0) {
      fetchRolesData(values.businessTypeId);
    } else if (selectedBusinessTypeId) {
      fetchRolesData([selectedBusinessTypeId]);
    }
    fetchRegionsData();
    fetchFrontlineData();
    return () => {
      // Clear context state on unmount
      if (qualificationsList.length > 0) {
        setQualificationsList([]);
      }
      if (passportTypeId) {
        setPassportTypeId("");
      }
      if (qualificationTypeId) {
        setQualificationTypeId("");
      }
    };
  }, []);

  useEffect(() => {
    if (regionId) {
      fetchFrontlineData();
    } else if (!regionId && (region || values.region)) {
      setRegionId(values.region || region);
    }
  }, [regionId, values.region]);

  // Fetch roles when business type changes
  useEffect(() => {
    if (values.businessTypeId.length > 0 && businessTypesList.length > 0) {
      fetchRolesData(values.businessTypeId);
    }
  }, [values.businessTypeId, businessTypesList.length]);

  /**
   * Whether to render a built-in field. Field names here match the catalog's
   * `formKey`, so this is a direct lookup. Defaults to shown, so the form is
   * never missing fields while the country's config is still loading, and
   * anything the catalog does not list as configurable is always shown.
   */
  const showBaseField = (fieldName: string): boolean => {
    const rule = baseRules[fieldName];
    return rule ? rule.isVisible : true;
  };

  /**
   * Whether a built-in field is mandatory for this country. `fallback` is the
   * form's own rule, used for fields the catalog does not make configurable and
   * while the country's config is still loading.
   */
  const requireBaseField = (fieldName: string, fallback = true): boolean => {
    const rule = baseRules[fieldName];
    return rule ? rule.isMandatory : fallback;
  };

  const fieldLabelMapping: { [key: string]: keyof InstallerFormValues } = {
    "First Name": "firstName",
    "Last Name": "lastName",

    Email: "emailId",
    // Only BASE fields belong here - the ones every country has. A country's
    // own fields are rendered from the field schema below, so adding one in
    // Country Onboarding needs no change to this list.
    // 'Phone Number': 'phoneNumber',
    // 'Emergency Contact': 'assessorName',
  };
  const textFieldLabels = [
    "First Name",
    "Last Name",

    "Email",
    // 'Phone Number',
    // 'Emergency Contact',
  ];

  return (
    <>
      <StyledDialog
        open={openDialog}
        onClose={handleCancel}
        fullWidth // Make the dialog occupy full width of its container
        maxWidth="md" // Set max width to 'md' or any other desired breakpoint (sm, md, lg, xl)
        sx={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }} // Optional: Set a minimum width if needed
      >
        <DialogTitle sx={{ paddingBottom: "6px", marginLeft: "10px" }}>
          <TextLabel type="titleRegular" lineHeight="27px">
            {t("adminDashboard.form.addInstaller.name")}
          </TextLabel>
        </DialogTitle>
        <DialogContent>
          {loading && <Loading />}
          {alertBoxState.type && (
            <div className="alert-box">
              <AlertBox
                type={alertBoxState.type}
                message={alertBoxState.message}
                onCloseCb={handleCloseAlert}
              />
            </div>
          )}
          {qualificationAlertMessage && (
            <div className="alert-box" style={{ marginTop: "10px" }}>
              <AlertBox
                type="error"
                message={qualificationAlertMessage}
                onCloseCb={() => setQualificationAlertMessage("")}
              />
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Left Section: Photo & Dropdown Fields */}
              <Grid item xs={6}>
                <Grid container direction="column" spacing={1}>
                  {/* Photo Upload */}
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Grid container spacing={2} sx={{ paddingLeft: "10px" }}>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <ImageUpload
                          photo={values.photo} // Pass existing photo prop if available
                          onImageChange={handleImageChange} // Pass handler to update form state
                          setAlertBoxState={setAlertBoxState}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={6}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <StatusCardProps
                          initialStatus={
                            values.status as "active" | "inactive" | "blocked"
                          }
                          onStatusChange={(status) =>
                            handleChange("status", status)
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Business Type Dropdown */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      sx={{
                        backgroundColor: "#e7eaf6",
                        borderRadius: 2,
                        marginTop: "16px",
                        maxWidth: "269px",
                      }}
                      required
                    >
                      <StyledInputLabel>
                        {t(
                          "adminDashboard.dropdownSelection.businessType",
                          "Business Type",
                        )}
                      </StyledInputLabel>
                      <Select
                        multiple
                        value={values.businessTypeId}
                        onChange={(e) => {
                          const selectedValues = Array.isArray(e.target.value)
                            ? e.target.value
                            : [];
                          handleChange("businessTypeId", selectedValues);
                        }}
                        renderValue={(selected) =>
                          selected
                            .map((id) =>
                              t(
                                `businessTypes.${
                                  businessTypesList?.find(
                                    (bt) => bt.businessTypeId === Number(id),
                                  )?.businessTypeName
                                }`,
                                businessTypesList?.find(
                                  (bt) => bt.businessTypeId === Number(id),
                                )?.businessTypeName || "",
                              ),
                            )
                            .filter((name) => name)
                            .join(", ")
                        }
                        sx={{
                          background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                          border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                          borderRadius: "8px",
                          maxHeight: "40px",
                          color: CONSTANTS.COLORS.TEXT_GRAY,
                        }}
                        required
                        error={!!errors.businessTypeId}
                      >
                        {businessTypesList?.map((bt) => (
                          <MenuItem
                            key={bt.businessTypeId}
                            value={bt.businessTypeId}
                            sx={{ color: CONSTANTS.COLORS.TEXT_GRAY }}
                          >
                            <Checkbox
                              checked={values.businessTypeId.includes(
                                bt.businessTypeId,
                              )}
                            />
                            {t(
                              `businessTypes.${bt.businessTypeName}`,
                              bt.businessTypeName,
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                      <TextLabel type="errorText">
                        {errors.businessTypeId ? errors.businessTypeId : ""}
                      </TextLabel>
                    </FormControl>
                  </Grid>

                  {/* Region Dropdown */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      sx={{
                        backgroundColor: "#e7eaf6",
                        borderRadius: 2,
                        marginTop: "16px",
                        maxWidth: "269px",
                      }}
                    >
                      <StyledInputLabel>
                        {t("adminDashboard.form.addInstaller.labels.area")}
                      </StyledInputLabel>
                      <Select
                        value={values.region || region}
                        onChange={(e) => handleChange("region", e.target.value)}
                        sx={{
                          background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                          border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                          borderRadius: "8px",
                          maxHeight: "40px",
                          color: CONSTANTS.COLORS.TEXT_GRAY,
                        }}
                        required
                        error={!!errors.region}
                      >
                        {regionList.map((regionItem) => (
                          <MenuItem
                            key={regionItem.region_id}
                            value={regionItem.region_id}
                            disabled={
                              regionItem.status === CONSTANTS.STATUS.INACTIVE
                            }
                            sx={{
                              color: CONSTANTS.COLORS.TEXT_GRAY,
                              textTransform: "capitalize",
                            }}
                          >
                            {regionItem.region_code}
                          </MenuItem>
                        ))}
                      </Select>
                      <TextLabel type="errorText">
                        {errors.region ? errors.region : ""}
                      </TextLabel>
                    </FormControl>
                  </Grid>

                  {/* Frontline Dropdown */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      sx={{
                        backgroundColor: "#e7eaf6",
                        borderRadius: 2,
                        marginTop: "16px",
                        maxWidth: "269px",
                      }}
                    >
                      <StyledInputLabel>
                        {t("adminDashboard.form.addInstaller.labels.frontline")}
                      </StyledInputLabel>
                      <Select
                        value={values.frontline || frontline}
                        onChange={(e) =>
                          handleChange("frontline", e.target.value)
                        }
                        sx={{
                          background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                          border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                          borderRadius: "8px",
                          maxHeight: "40px",
                          color: CONSTANTS.COLORS.TEXT_GRAY,
                        }}
                        required
                        error={!!errors.frontline}
                      >
                        {frontlineList.map((frontlineItem) => (
                          <MenuItem
                            key={frontlineItem.frontline_id}
                            value={frontlineItem.frontline_id}
                            disabled={
                              frontlineItem.status === CONSTANTS.STATUS.INACTIVE
                            }
                            sx={{ color: CONSTANTS.COLORS.TEXT_GRAY }}
                          >
                            {frontlineItem.frontline_code}
                          </MenuItem>
                        ))}
                      </Select>
                      <TextLabel type="errorText">
                        {errors.frontline ? errors.frontline : ""}
                      </TextLabel>
                    </FormControl>
                  </Grid>

                  {/* Country Dropdown */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      sx={{
                        backgroundColor: "#e7eaf6",
                        borderRadius: 2,
                        marginTop: "10px",
                        maxWidth: "269px",
                      }}
                    >
                      <StyledInputLabel>
                        {t("adminDashboard.form.addInstaller.labels.country")}
                      </StyledInputLabel>
                      <Select
                        value={values.country}
                        onChange={(e) =>
                          handleChange("country", e.target.value)
                        }
                        sx={{
                          background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                          border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                          borderRadius: "8px",
                          maxHeight: "40px",
                          color: CONSTANTS.COLORS.TEXT_GRAY,
                        }}
                        required
                        error={!!errors.country}
                      >
                        {countryList.map((country) => (
                          <MenuItem
                            key={country.country_id}
                            value={country.country_id}
                            disabled={
                              country.status === CONSTANTS.STATUS.INACTIVE
                            }
                            sx={{ color: CONSTANTS.COLORS.TEXT_GRAY }}
                          >
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <TextLabel type="errorText">
                        {errors.country ? errors.country : ""}
                      </TextLabel>
                    </FormControl>
                  </Grid>

                  {/* Qualified Dropdown */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      sx={{
                        backgroundColor: "#e3f2fd",
                        marginTop: "10px",
                        maxWidth: "269px",
                      }}
                    >
                      <StyledInputLabel>
                        {t(
                          "adminDashboard.form.addInstaller.labels.qualifiedToWork",
                        )}
                      </StyledInputLabel>
                      <Select
                        multiple
                        value={values.qualified} // Make sure this is an array
                        onChange={(e) =>
                          handleChange("qualified", e.target.value)
                        } // Handle multiple values
                        renderValue={(selected) => {
                          // Display the names of the selected countries
                          return selected
                            .map((countryId) => {
                              const country = countryList.find(
                                (c) => c.country_id === countryId,
                              );
                              return country ? country.name : "";
                            })
                            .join(", "); // Join names with commas
                        }}
                        sx={{
                          background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                          border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                          borderRadius: "8px",
                          maxHeight: "40px",
                        }}
                      >
                        {countryList
                          .filter(
                            (country) =>
                              country.frontline_id === values.frontline,
                          )
                          .map((country) => (
                            <MenuItem
                              key={country.country_id}
                              value={country.country_id}
                              disabled={
                                country.status === CONSTANTS.STATUS.INACTIVE
                              }
                              sx={{ color: CONSTANTS.COLORS.TEXT_GRAY }}
                            >
                              <Checkbox
                                checked={values.qualified.includes(
                                  country.country_id,
                                )}
                              />
                              {country.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* Right Section: Personal Information */}
              <Grid
                item
                xs={6}
                style={{ paddingLeft: "2.97vw", marginTop: "8px" }}
              >
                <Box sx={{ borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    {/* Personal Info Fields */}
                    {textFieldLabels
                      .filter((label) => showBaseField(fieldLabelMapping[label]))
                      .map((label) => {
                      const field = fieldLabelMapping[label];
                      const errorMessage = errors[field];
                      return (
                        <Grid item xs={12} key={label}>
                          <StyledTextField
                            label={t(
                              `adminDashboard.form.addInstaller.labels.${field}`,
                            )}
                            fullWidth
                            value={values[field]}
                            onChange={(e) =>
                              handleChange(field, e.target.value)
                            }
                            sx={{
                              backgroundColor: "#e7eaf6",
                              borderRadius: 2,
                              maxWidth: "269px",
                            }}
                            // A country's config may only ADD a requirement, never
                            // drop below what the server enforces: `onboarding.ts`
                            // rejects a KONE internal user with no email
                            // regardless of what the country config says. So the
                            // server rule is the floor and the country rule can
                            // tighten it (e.g. also requiring it for vendors).
                            // Name fields are not configurable, so stay required.
                            required={
                              field === "emailId"
                                ? supplierType !== "vendor" ||
                                  requireBaseField("emailId", false)
                                : true
                            }
                            error={!!errorMessage}
                            helperText={errorMessage}
                          />
                        </Grid>
                      );
                    })}
                    {showBaseField("contactNumber") && (
                    <Grid item xs={12}>
                      <StyledTextField
                        label={t(
                          "adminDashboard.form.addInstaller.labels.phoneNumber",
                        )}
                        fullWidth
                        value={values.contactNumber}
                        onChange={(e) =>
                          handleChange("contactNumber", e.target.value)
                        }
                        sx={{
                          backgroundColor: "#e7eaf6",
                          borderRadius: 2,
                          maxWidth: "269px",
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {selectedCountry &&
                                countryList.find(
                                  (country) =>
                                    country.country_code === selectedCountry,
                                )?.phone_code}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    )}
                    {supplierType !== "vendor" && (
                      <>
                        {showBaseField("assessorName") && (
                        <Grid item xs={12}>
                          <StyledTextField
                            label={t(
                              "adminDashboard.form.addInstaller.labels.assessorName",
                            )}
                            fullWidth
                            value={values.assessorName}
                            onChange={(e) =>
                              handleChange("assessorName", e.target.value)
                            }
                            sx={{
                              backgroundColor: "#e7eaf6",
                              borderRadius: 2,
                              maxWidth: "269px",
                            }}
                            required={requireBaseField("assessorName")}
                          />
                        </Grid>
                        )}

                        {showBaseField("koneEmployeeId") && (
                        <Grid item xs={12}>
                          <StyledTextField
                            label={t(
                              "adminDashboard.form.addInstaller.labels.koneEmployeeId",
                            )}
                            fullWidth
                            value={values.koneEmployeeId}
                            onChange={(e) =>
                              handleChange("koneEmployeeId", e.target.value)
                            }
                            sx={{
                              backgroundColor: "#e7eaf6",
                              borderRadius: 2,
                              maxWidth: "269px",
                            }}
                            required={requireBaseField("koneEmployeeId")}
                            error={!!errors.koneEmployeeId}
                            helperText={errors.koneEmployeeId}
                          />
                        </Grid>
                        )}
                      </>
                    )}
                    {/* Company Dropdown */}
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        sx={{
                          backgroundColor: "#e7eaf6",
                          borderRadius: 2,
                          marginTop: "10px",
                          maxWidth: "269px",
                        }}
                      >
                        <Autocomplete
                          options={companyList || []}
                          getOptionLabel={(option) => option.firstName}
                          sx={{
                            background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                            border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                            borderRadius: "8px",
                            maxHeight: "40px",
                            color: CONSTANTS.COLORS.TEXT_GRAY,
                          }}
                          value={
                            companyList?.find(
                              (company) => company.id === values.company,
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            handleChange(
                              "company",
                              newValue ? newValue.id : "",
                            );
                          }}
                          renderInput={(params) => (
                            <StyledTextField
                              {...params}
                              label={t(
                                "adminDashboard.form.addInstaller.labels.company",
                              )}
                              sx={{
                                background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                                border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                                borderRadius: "8px",
                                maxHeight: "40px",
                              }}
                              required
                              error={!!errors.company}
                              helperText={errors.company}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        sx={{
                          backgroundColor: "#e7eaf6",
                          borderRadius: 2,
                          maxWidth: "269px",
                        }}
                        required
                      >
                        <Autocomplete
                          options={installerRoleList || []}
                          getOptionLabel={(option) => option.role_name}
                          sx={{
                            background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                            border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                            borderRadius: "8px",
                            maxHeight: "40px",
                            color: CONSTANTS.COLORS.TEXT_GRAY,
                          }}
                          value={
                            installerRoleList?.find(
                              (role) => role.role_id === values.role,
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            handleChange(
                              "role",
                              newValue ? newValue.role_id : "",
                            );
                          }}
                          renderInput={(params) => (
                            <StyledTextField
                              {...params}
                              label={t(
                                "adminDashboard.form.addInstaller.labels.jobTitle",
                              )}
                              sx={{
                                background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                                border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                                borderRadius: "8px",
                                maxHeight: "40px",
                              }}
                              required
                              error={!!errors.role}
                              helperText={errors.role}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    {/* Manager (foreman). Subcontractor employees only - a KONE
                        employee's manager comes from Workday. The candidates
                        belong to the selected vendor, so the vendor is chosen
                        first and this stays disabled until it is. */}
                    {supplierType === "vendor" && (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          sx={{
                            backgroundColor: "#e7eaf6",
                            borderRadius: 2,
                            maxWidth: "269px",
                          }}
                        >
                          <Autocomplete
                            options={managerList || []}
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                key={option.id}
                                title={
                                  option.smpEmployeeId
                                    ? `${option.name} (${option.smpEmployeeId})`
                                    : option.name
                                }
                              >
                                {option.name}
                              </li>
                            )}
                            filterOptions={(options, { inputValue }) => {
                              // The SMP id used to be part of the label, which made
                              // it searchable. It only shows on hover now, so match
                              // on it explicitly rather than quietly losing that.
                              const query = inputValue.trim().toLowerCase();
                              if (!query) return options;
                              return options.filter((option) =>
                                `${option.name} ${option.smpEmployeeId || ""}`
                                  .toLowerCase()
                                  .includes(query),
                              );
                            }}
                            disabled={!values.company}
                            loading={managersLoading}
                            noOptionsText={t(
                              "adminDashboard.form.addInstaller.labels.noManagersFound",
                            )}
                            sx={{
                              background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                              border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                              borderRadius: "8px",
                              maxHeight: "40px",
                              color: CONSTANTS.COLORS.TEXT_GRAY,
                            }}
                            value={
                              managerList?.find(
                                (manager) => manager.id === values.managerId,
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              handleChange(
                                "managerId",
                                newValue ? newValue.id : "",
                              );
                            }}
                            renderInput={(params) => (
                              <StyledTextField
                                {...params}
                                label={t(
                                  "adminDashboard.form.addInstaller.labels.manager",
                                )}
                                helperText={
                                  values.company
                                    ? undefined
                                    : t(
                                        "adminDashboard.form.addInstaller.labels.managerSelectVendorFirst",
                                      )
                                }
                                sx={{
                                  background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                                  border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                                  borderRadius: "8px",
                                  maxHeight: "40px",
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                    )}
                    {values.role ===
                      installerRoleList.find(
                        (role) => role.role_name === "Others",
                      )?.role_id && (
                      <Grid item xs={12}>
                        <StyledTextField
                          label={t(
                            "adminDashboard.form.addInstaller.labels.customRoleName",
                          )}
                          fullWidth
                          value={values.customRoleName || ""} // Use a new `customRoleName` field for the description
                          onChange={(e) =>
                            handleChange("customRoleName", e.target.value)
                          }
                          sx={{
                            backgroundColor: "#e7eaf6",
                            borderRadius: 2,
                            maxWidth: "269px",
                          }}
                          required
                        />
                      </Grid>
                    )}

                    {/* Dynamic Fields */}
                    {dynamicFields.length > 0 &&
                      dynamicFields.map((field: any) => (
                        <Grid item xs={12} key={field.fieldKey}>
                          {field.fieldType === "text" && (
                            <StyledTextField
                              label={field.displayName}
                              fullWidth
                              value={dynamicFieldValues[field.fieldKey] || ""}
                              onChange={(e) =>
                                handleDynamicFieldChange(
                                  field.fieldKey,
                                  e.target.value,
                                  field,
                                )
                              }
                              sx={{
                                backgroundColor: "#e7eaf6",
                                borderRadius: 2,
                                maxWidth: "269px",
                              }}
                              required={field.isMandatory === 1}
                              error={!!dynamicFieldErrors[field.fieldKey]}
                              helperText={
                                dynamicFieldErrors[field.fieldKey] || ""
                              }
                              inputProps={{
                                maxLength: field.charLimit || undefined,
                              }}
                            />
                          )}
                          {field.fieldType === "date" && (
                            <StyledTextField
                              label={field.displayName}
                              type="date"
                              fullWidth
                              value={dynamicFieldValues[field.fieldKey] || ""}
                              onChange={(e) =>
                                handleDynamicFieldChange(
                                  field.fieldKey,
                                  e.target.value,
                                  field,
                                )
                              }
                              sx={{
                                backgroundColor: "#e7eaf6",
                                borderRadius: 2,
                                maxWidth: "269px",
                              }}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              required={field.isMandatory === 1}
                              error={!!dynamicFieldErrors[field.fieldKey]}
                              helperText={dynamicFieldErrors[field.fieldKey]}
                            />
                          )}
                          {field.fieldType === "number" && (
                            <StyledTextField
                              label={field.displayName}
                              type="number"
                              fullWidth
                              value={dynamicFieldValues[field.fieldKey] || ""}
                              onChange={(e) =>
                                handleDynamicFieldChange(
                                  field.fieldKey,
                                  e.target.value,
                                  field,
                                )
                              }
                              sx={{
                                backgroundColor: "#e7eaf6",
                                borderRadius: 2,
                                maxWidth: "269px",
                              }}
                              required={field.isMandatory === 1}
                              error={!!dynamicFieldErrors[field.fieldKey]}
                              helperText={dynamicFieldErrors[field.fieldKey]}
                            />
                          )}
                          {field.fieldType === "dropdown" && (
                            <FormControl
                              fullWidth
                              required={field.isMandatory === 1}
                              error={!!dynamicFieldErrors[field.fieldKey]}
                              sx={{ maxWidth: "269px" }}
                            >
                              <StyledInputLabel shrink>
                                {field.displayName}
                              </StyledInputLabel>
                              <Select
                                value={dynamicFieldValues[field.fieldKey] || ""}
                                displayEmpty
                                onChange={(e) =>
                                  handleDynamicFieldChange(
                                    field.fieldKey,
                                    e.target.value,
                                    field,
                                  )
                                }
                                sx={{
                                  backgroundColor: "#e7eaf6",
                                  borderRadius: 2,
                                }}
                              >
                                {(field.fieldOptions || []).map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                          {field.fieldType === "checkbox" && (
                            <Box
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Checkbox
                                checked={
                                  dynamicFieldValues[field.fieldKey] === true ||
                                  dynamicFieldValues[field.fieldKey] === "true"
                                }
                                onChange={(e) =>
                                  handleDynamicFieldChange(
                                    field.fieldKey,
                                    e.target.checked,
                                    field,
                                  )
                                }
                              />
                              <TextLabel type="regularText">
                                {field.displayName}
                                {field.isMandatory === 1 ? " *" : ""}
                              </TextLabel>
                            </Box>
                          )}
                        </Grid>
                      ))}

                    {/* Comments Field */}
                    {showBaseField("comments") && (
                    <Grid item xs={12}>
                      <StyledTextField
                        label={t(
                          "adminDashboard.form.addInstaller.labels.comments",
                        )}
                        fullWidth
                        rows={4}
                        value={values.comments}
                        onChange={(e) =>
                          handleChange("comments", e.target.value)
                        }
                        sx={{
                          backgroundColor: "#e7eaf6",
                          borderRadius: 2,
                          maxWidth: "269px",
                        }}
                        required={requireBaseField("comments")}
                      />
                    </Grid>
                    )}

                    {/* Action Buttons */}
                  </Grid>
                </Box>
              </Grid>
              {/* Training List */}
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  padding: 0,
                  margin: 0,
                }}
              >
                <TrainingList
                  qualifiedToWorkCountry={values.qualified}
                  onEditClick={handleQualificationEditClick}
                  profileFormData={values}
                  selectedCountryId={values.country}
                  countryList={countryList}
                />
              </Grid>
              {/* Action Buttons */}
              <Grid
                container
                spacing={3}
                justifyContent="center"
                sx={{ mt: 2 }}
              >
                <Grid item xs={5}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center", // Center the buttons horizontally
                      gap: 2, // Add spacing between the buttons
                      mt: 2, // Add margin-top
                    }}
                  >
                    <StyledButtons.SecondaryButton onClick={handleCancel}>
                      {t("adminDashboard.form.button.cancel")}
                    </StyledButtons.SecondaryButton>
                    <StyledButtons.PrimaryButton
                      type="submit"
                      onClick={handleSubmit}
                    >
                      {t("adminDashboard.form.button.save")}
                    </StyledButtons.PrimaryButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </form>
          {showAlert && (
            <Alert severity="success" style={{ marginBottom: "16px" }}>
              {successMessage}
            </Alert>
          )}
        </DialogContent>
      </StyledDialog>
      {credentials && (
        <CredentialsDialog
          credentials={credentials}
          onClose={handleCredentialsClose}
        />
      )}
    </>
  );
};

export default AdminInstallerForm;
